import { DeleteRounded, UploadFileRounded } from '@mui/icons-material'
import {
    Box,
    Button,
    Chip,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    FormLabel,
    IconButton,
    Input,
    ModalClose,
    ModalDialog,
    Stack,
    Textarea,
    Typography,
} from '@mui/joy'
import { useApi } from 'api'
import { useUserData } from 'auth'
import Modal, { dontCloseOnBackgroundClick } from 'components/modal'
import { useShowSnack } from 'components/snackbar'
import { prop } from 'ramda'
import { useState } from 'react'
import { useListingContext } from '../listingProvider'

interface Props {
    open: boolean
    onClose: (data?: { editUrl: string; documentId: string }) => void
    title?: string
}

interface CreateDocumentData {
    name: string
    emailSubject?: string
    emailMessage?: string
    selectedRecipients: {
        agent: boolean
        vendors: boolean
        buyers: boolean
    }
    files: File[]
}

export default function CreateDocumentModal({ open, onClose, title = 'Create New Document' }: Props) {
    const { listing, setListing } = useListingContext()
    const { user } = useUserData()
    const showSnack = useShowSnack()
    const api = useApi()

    const [loading, setLoading] = useState(false)
    const [formData, setFormData] = useState<CreateDocumentData>({
        name: '',
        emailSubject: '',
        emailMessage: '',
        selectedRecipients: {
            agent: false,
            vendors: false,
            buyers: false,
        },
        files: [],
    })

    const handleSubmit = async () => {
        if (!formData.name.trim()) {
            showSnack('Document name is required', 'danger')
            return
        }

        // Build recipients from selected groups
        const recipients: { name: string; email: string; roleName: string }[] = []

        if (formData.selectedRecipients.agent && listing.agents?.[0]) {
            // For now, we'll use the current user as the agent
            // In a real implementation, you'd fetch the agent details by userId
            recipients.push({
                name: `${user.firstName} ${user.lastName}`,
                email: user.email,
                roleName: 'Agent',
            })
        }

        if (formData.selectedRecipients.vendors && listing.vendors) {
            listing.vendors.forEach((vendor, index) => {
                recipients.push({
                    name: `${vendor.firstName} ${vendor.lastName}`,
                    email: vendor.email,
                    roleName: `Vendor ${index + 1}`,
                })
            })
        }

        if (formData.selectedRecipients.buyers && listing.contract?.buyers) {
            listing.contract.buyers.forEach((buyer, index) => {
                recipients.push({
                    name: `${buyer.firstName} ${buyer.lastName}`,
                    email: buyer.email,
                    roleName: `Buyer ${index + 1}`,
                })
            })
        }

        if (recipients.length === 0) {
            showSnack('At least one recipient group must be selected', 'danger')
            return
        }

        setLoading(true)
        try {
            // Convert files to base64
            const base64Files = await Promise.all(
                formData.files.map(async file => {
                    return new Promise<{ name: string; base64: string }>((resolve, reject) => {
                        const reader = new FileReader()
                        reader.onload = () => {
                            const base64 = reader.result as string
                            // Remove the data:application/pdf;base64, prefix
                            const base64Data = base64.split(',')[1]
                            resolve({
                                name: file.name,
                                base64: base64Data,
                            })
                        }
                        reader.onerror = reject
                        reader.readAsDataURL(file)
                    })
                }),
            )

            const { updatedListing, editUrl, envelopeId } = await api
                .post(`/listing/sale/${listing.listingId}/annature/generic-documents/create`, {
                    name: formData.name,
                    emailSubject: formData.emailSubject,
                    emailMessage: formData.emailMessage,
                    recipients: recipients,
                    files: base64Files,
                })
                .then(prop('data'))

            setListing(updatedListing)
            showSnack('Document created successfully', 'success')
            onClose({ editUrl, documentId: envelopeId })

            // Reset form
            setFormData({
                name: '',
                emailSubject: '',
                emailMessage: '',
                selectedRecipients: {
                    agent: false,
                    vendors: false,
                    buyers: false,
                },
                files: [],
            })
        } catch (error) {
            console.error('Error creating document:', error)
            showSnack('Error creating document', 'danger')
        } finally {
            setLoading(false)
        }
    }

    const toggleRecipient = (type: keyof typeof formData.selectedRecipients) => {
        setFormData(prev => ({
            ...prev,
            selectedRecipients: {
                ...prev.selectedRecipients,
                [type]: !prev.selectedRecipients[type],
            },
        }))
    }

    const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
        const files = Array.from(event.target.files || [])
        const pdfFiles = files.filter(file => file.type === 'application/pdf')

        if (pdfFiles.length !== files.length) {
            showSnack('Only PDF files are allowed', 'warning')
        }

        setFormData(prev => ({
            ...prev,
            files: [...prev.files, ...pdfFiles],
        }))
    }

    const removeFile = (index: number) => {
        setFormData(prev => ({
            ...prev,
            files: prev.files.filter((_, i) => i !== index),
        }))
    }

    return (
        <Modal
            open={open}
            onClose={dontCloseOnBackgroundClick(onClose)}
        >
            <ModalDialog
                sx={{
                    minWidth: 600,
                    minHeight: 450,
                }}
            >
                <ModalClose />
                <DialogTitle>{title}</DialogTitle>
                <DialogContent>
                    <Box sx={{ px: 1, mt: 1 }}>
                        <Stack spacing={3}>
                            <FormControl>
                                <FormLabel>Document Name *</FormLabel>
                                <Input
                                    value={formData.name}
                                    onChange={e => setFormData(prev => ({ ...prev, name: e.target.value }))}
                                    placeholder="Enter document name"
                                />
                            </FormControl>

                            <FormControl>
                                <FormLabel>Email Subject</FormLabel>
                                <Input
                                    value={formData.emailSubject}
                                    onChange={e =>
                                        setFormData(prev => ({ ...prev, emailSubject: e.target.value }))
                                    }
                                    placeholder="Enter email subject (optional)"
                                />
                            </FormControl>

                            <FormControl>
                                <FormLabel>Email Message</FormLabel>
                                <Textarea
                                    value={formData.emailMessage}
                                    onChange={e =>
                                        setFormData(prev => ({ ...prev, emailMessage: e.target.value }))
                                    }
                                    placeholder="Enter email message (optional)"
                                    minRows={3}
                                />
                            </FormControl>

                            <Box>
                                <Typography
                                    level="title-md"
                                    sx={{ mb: 2 }}
                                >
                                    PDF Files
                                </Typography>
                                <Stack spacing={2}>
                                    {formData.files.map((file, index) => (
                                        <Box
                                            key={index}
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'space-between',
                                                border: '1px solid',
                                                borderColor: 'divider',
                                                borderRadius: 'sm',
                                                p: 2,
                                            }}
                                        >
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <UploadFileRounded />
                                                <Typography>{file.name}</Typography>
                                                <Chip
                                                    size="sm"
                                                    variant="soft"
                                                >
                                                    {(file.size / 1024 / 1024).toFixed(2)} MB
                                                </Chip>
                                            </Box>
                                            <IconButton
                                                size="sm"
                                                color="danger"
                                                onClick={() => removeFile(index)}
                                            >
                                                <DeleteRounded />
                                            </IconButton>
                                        </Box>
                                    ))}
                                    <Button
                                        variant="outlined"
                                        component="label"
                                        startDecorator={<UploadFileRounded />}
                                        sx={{ alignSelf: 'flex-start' }}
                                    >
                                        Upload PDF Files
                                        <input
                                            type="file"
                                            multiple
                                            accept=".pdf"
                                            style={{ display: 'none' }}
                                            onChange={handleFileUpload}
                                        />
                                    </Button>
                                </Stack>
                            </Box>

                            <Box>
                                <Typography
                                    level="title-md"
                                    sx={{ mb: 2 }}
                                >
                                    Recipients *
                                </Typography>
                                <Stack spacing={2}>
                                    <Box
                                        sx={{
                                            border: '1px solid',
                                            borderColor: 'divider',
                                            borderRadius: 'sm',
                                            p: 2,
                                        }}
                                    >
                                        <Stack spacing={2}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                                <input
                                                    type="checkbox"
                                                    id="agent"
                                                    checked={formData.selectedRecipients.agent}
                                                    onChange={() => toggleRecipient('agent')}
                                                />
                                                <label htmlFor="agent">
                                                    <Typography>Agent</Typography>
                                                </label>
                                            </Box>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                                <input
                                                    type="checkbox"
                                                    id="vendors"
                                                    checked={formData.selectedRecipients.vendors}
                                                    onChange={() => toggleRecipient('vendors')}
                                                />
                                                <label htmlFor="vendors">
                                                    <Typography>
                                                        Vendors ({listing.vendors?.length || 0})
                                                    </Typography>
                                                </label>
                                            </Box>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                                <input
                                                    type="checkbox"
                                                    id="buyers"
                                                    checked={formData.selectedRecipients.buyers}
                                                    onChange={() => toggleRecipient('buyers')}
                                                />
                                                <label htmlFor="buyers">
                                                    <Typography>
                                                        Buyers ({listing.contract?.buyers?.length || 0})
                                                    </Typography>
                                                </label>
                                            </Box>
                                        </Stack>
                                    </Box>
                                </Stack>
                            </Box>
                        </Stack>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button
                        loading={loading}
                        onClick={handleSubmit}
                        disabled={
                            formData.name.trim() == '' ||
                            formData.files.length == 0 ||
                            Object.values(formData.selectedRecipients).every(value => !value)
                        }
                    >
                        Create Document
                    </Button>
                    <Button
                        variant="outlined"
                        onClick={() => onClose()}
                    >
                        Cancel
                    </Button>
                </DialogActions>
            </ModalDialog>
        </Modal>
    )
}
